import React from "react";

export interface CalendarEvent {
  quarter: string;
  reviewDate: string; // YYYY-MM-DD
  prereadDeadline: string;
  platform: string;
  partnership: string;
  pointPerson: string;
  budget: number | null;
  notes: string;
}

export interface OOO {
  name: string;
  startDate: string;
  endDate: string;
  reason: string;
}
/**
 *  Safe Lazy Loader
 */
const safeLazy = (importFn: any, exportName?: string) =>
  React.lazy(async () => {
    const module = await importFn();

    if (exportName) {
      if (!module[exportName]) {
        throw new Error(`Component ${exportName} not found`);
      }
      return { default: module[exportName] };
    }

    if (!module.default) {
      throw new Error("Default export not found");
    }

    return { default: module.default };
  });

export type WidgetConfig = {
  component: keyof typeof widgetComponentMap;
  layout: string;
  title?: string;
};

export type WidgetProps = {
  userUpn: string;
};
/**
 * Component Map
 */
export const widgetComponentMap: Record<
  string,
  React.LazyExoticComponent<React.ComponentType<WidgetProps>>
> = {
  QuickAccessApps: safeLazy(() => import("@/Components/Widgets/QuickAccessApps")),
  ActiveProgramsWidget: safeLazy(() => import("@/features/ActivePrograms"), "ActiveProgramsWidget"),
  TaskPriorities: safeLazy(() => import("@/Components/Widgets/TaskPriorities"), "TaskPriorities"),
  NotificationsPanel: safeLazy(
    () => import("@/features/Notifications/component/NotificationsPanel"),
    "NotificationsPanel"
  ),
  KeyGuidelines: safeLazy(() => import("@/Components/Widgets/KeyGuidelines"), "KeyGuidelines"),
  MeetingsWidget: safeLazy(
    () => import("@/features/Upcoming-meetings/MeetingsWidget"),
    "MeetingsWidget"
  ),
  ImpactTargets: safeLazy(
    () => import("@/features/ImpactTargets/components/ImpactTargets"),
    "ImpactTargets"
  ),
  TeamCalendarWidget: safeLazy(
    () => import("@/Components/TeamCalendar/TeamCalendarWidget"),
    "TeamCalendarWidget"
  ),
  PartnersDirectory: safeLazy(() => import("@/Components/Widgets/partnersDirectory")),
  LearningInsights: safeLazy(() => import("@/Components/Widgets/LearningInsights")),
  AnalyticsWidget: safeLazy(() => import("@/features/AnalyticsInsights/component/AnalyticsWidget")),
  // CompassWidget: safeLazy(() => import("@/backup/Compass/CompassWidget")),
  // PartnerIssuesRisksWidget: safeLazy(
  //   () => import("@/backup/partnerIssuesRisks/PartnerIssuesRisksWidget"),
  //   "PartnerIssuesRisksWidget"
  // ),
  // ProgramMetrics: safeLazy(() => import("@/backup/programMetrics/component/ProgramMetrics")),
  ProgramResources: safeLazy(
    () => import("@/Components/Widgets/ProgramResources"),
    "ProgramResources"
  ),
  OversightWidget: safeLazy(() => import("@/Components/Oversightwidget/OversightWidget")),
  PipelinePriority: safeLazy(() => import("@/features/PipelinePriority/PipelinePriority")),
  // SubmissionsWidget: safeLazy(
  //   () => import("@/backup/Submission/SubmissionsWidget"),
  //   "SubmissionsWidget"
  // ),
  // PaymentsPipelineWidget: safeLazy(
  //   () => import("@/backup/PaymentsPipelineWidget"),
  //   "PaymentsPipelineWidget"
  // ),
  PartnerSubmissions: safeLazy(
    () => import("@/features/partnerSubmission/component/PartnerSubmissions")
  ),
  DisbursementRequestsWrapper: safeLazy(
    () => import("@/Components/Widgets/DisbursementRequestsWrapper")
  ),
  WorldClock: safeLazy(() => import("@/Components/TimezoneBar")),
  Accessibility: safeLazy(() => import("@/features/AccessibilityPanel/AccessibilityPanel")),
  AccountSettings: safeLazy(() => import("@/features/Navbar/components/ProfileModal")),
  IdeasTracker: safeLazy(() => import("@/Components/IdeasTracker")),
  ProgramImpact: safeLazy(() => import("@/Components/Widgets/ProgramImpact")),
};
export const featureToWidgetMap: Record<string, string> = {
  "dashboard.quiklinks": "QuickAccessApps",
  "dashboard.myactiveitems": "ActiveProgramsWidget",
  "dashboard.taskpriorities": "TaskPriorities",
  "dashboard.notifications": "NotificationsPanel",
  "dashboard.keyguidelines": "KeyGuidelines",
  "dashboard.upcomingmeetings": "MeetingsWidget",
  "dashboard.impacttargets": "ImpactTargets",
  "dashboard.teamcalender": "TeamCalendarWidget",
  "dashboard.partnerdirectory": "PartnersDirectory",
  "dashboard.learninginsights": "LearningInsights",
  "dashboard.analyticsinsights": "AnalyticsWidget",
  "dashboard.programresources": "ProgramResources",
  "dashboard.oversight": "OversightWidget",
  "dashboard.pipelinepriority": "PipelinePriority",
  "dashboard.pipeline-priority": "PipelinePriority",
  "dashboard.submissions": "PartnerSubmissions",
  "dashboard.partnersubmissions": "PartnerSubmissions",
  "dashboard.disbursementrequest": "DisbursementRequestsWrapper",
  "dashboard.ideatracker": "IdeasTracker",
  "dashboard.programimpact": "ProgramImpact",
};

export const widgetKeyToRoute: Record<string, string> = {
  QuickAccessApps: "quick-access",
  ActiveProgramsWidget: "active-programs",
  TaskPriorities: "task-priorities",
  NotificationsPanel: "notifications",
  KeyGuidelines: "guidelines",
  MeetingsWidget: "meetings",
  ImpactTargets: "impact-targets",
  TeamCalendarWidget: "team-calendar",
  PartnersDirectory: "partners",
  LearningInsights: "learning",
  AnalyticsWidget: "analytics",
  ProgramResources: "resources",
  OversightWidget: "oversight",
  PipelinePriority: "pipeline-priority",
  PartnerSubmissions: "submissions",
  DisbursementRequestsWrapper: "disbursements",
  WorldClock: "world-clocks",
  Accessibility: "accessibility",
  AccountSettings: "account-settings",
  IdeasTracker: "ideas-tracker",
  ProgramImpact: "program-impact",
};
